// Garde de navigation — §6bis/§6ter du contrat gelé `CONTRACT-AUTHENTIFICATION.md`.
// Tant que `GET /api/auth/moi` répond 401, l'app affiche la connexion et rien
// d'autre (cf. App.tsx). Ce fichier ne fait QUE gérer l'état de session côté
// front ; l'API refuse indépendamment (403) tout écran/action non autorisé —
// aucune logique de sécurité ne doit reposer sur ce contexte.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { z } from 'zod';
import { api, ApiError } from './api-client';
import { LoginResponseSchema, MoiResponseSchema, type Utilisateur } from './auth-types';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  readonly status: AuthStatus;
  readonly utilisateur: Utilisateur | null;
  /** Message français si la vérification de session au démarrage a échoué pour une raison AUTRE qu'un 401 (ex. serveur injoignable). */
  readonly bootError: string | null;
  /** Rejette avec une Error au message déjà traduit en français, prêt à afficher tel quel. */
  readonly login: (email: string, motDePasse: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Traduit une erreur technique en message français compréhensible.
 * Jamais de code d'erreur, de statut HTTP ni de détail technique affiché —
 * règle non négociable §5.3 du contrat (pas d'énumération de comptes, pas de
 * jargon pour Virginie).
 */
function messageErreurAuth(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Identifiants incorrects.';
    if (err.code === 'NETWORK_ERROR') {
      return 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.';
    }
  }
  return 'Une erreur est survenue. Réessayez dans un instant.';
}

/**
 * Brouillons de saisie gardés en `sessionStorage` par les modules (réception
 * `herbo-commandes`, clé par commande) : jamais restaurés chez un AUTRE compte
 * du même onglet (réfutation 29/09). Purgés à la déconnexion, et à la
 * connexion d'un compte différent du dernier connecté dans l'onglet (session
 * expirée sans déconnexion). Les modules n'ont pas accès au compte connecté
 * (zéro import de l'app) : le garde vit donc ici. Stockage indisponible →
 * silencieux, jamais bloquant.
 */
const PREFIXES_BROUILLONS = ['herbo-commandes:reception-brouillon:'];
const CLE_PROPRIETAIRE_BROUILLONS = 'herbo-web:brouillons-proprietaire';

function purgerBrouillons(): void {
  try {
    const s = window.sessionStorage;
    const cles: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && PREFIXES_BROUILLONS.some((p) => k.startsWith(p))) cles.push(k);
    }
    for (const k of cles) s.removeItem(k);
    s.removeItem(CLE_PROPRIETAIRE_BROUILLONS);
  } catch {
    /* stockage indisponible : rien à purger */
  }
}

function noterProprietaireBrouillons(idUtilisateur: number): void {
  try {
    if (window.sessionStorage.getItem(CLE_PROPRIETAIRE_BROUILLONS) !== String(idUtilisateur)) purgerBrouillons();
    window.sessionStorage.setItem(CLE_PROPRIETAIRE_BROUILLONS, String(idUtilisateur));
  } catch {
    /* stockage indisponible */
  }
}

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  const checkSession = useCallback(() => {
    setStatus('loading');
    setBootError(null);
    api
      .get('/auth/moi', MoiResponseSchema)
      .then(({ utilisateur: u }) => {
        noterProprietaireBrouillons(u.id);
        setUtilisateur(u);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        setUtilisateur(null);
        setStatus('unauthenticated');
        // 401 = pas encore connecté, cas normal, pas d'erreur à afficher.
        if (!(err instanceof ApiError && err.status === 401)) {
          setBootError(messageErreurAuth(err));
        }
      });
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (email: string, motDePasse: string): Promise<void> => {
    try {
      const { utilisateur: u } = await api.post('/auth/login', LoginResponseSchema, { email, motDePasse });
      noterProprietaireBrouillons(u.id);
      setUtilisateur(u);
      setStatus('authenticated');
      setBootError(null);
    } catch (err) {
      throw new Error(messageErreurAuth(err));
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout', z.undefined());
    } catch {
      // On déconnecte côté client même si l'appel réseau échoue — inutile
      // d'en informer l'utilisateur, rien ne le bloque.
    } finally {
      purgerBrouillons();
      setUtilisateur(null);
      setStatus('unauthenticated');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, utilisateur, bootError, login, logout, retry: checkSession }),
    [status, utilisateur, bootError, login, logout, checkSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() doit être appelé sous <AuthProvider>.');
  }
  return ctx;
}
